import {
  Component,
  ElementRef,
  OnDestroy,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import type { Canvas, Rect } from 'fabric';
import { I18N, TranslatePipe } from 'O3_core';
import { paletteColour } from '../../shared/palette.utils';
import enTranslations from '../locales/en/resources.json';
import roTranslations from '../locales/ro/resources.json';

/**
 * What the redactor produces: the flattened image, and how many areas were
 * covered to make it. The count travels with the file so the upload records
 * both together and neither can be reported without the other.
 */
export interface IRedactedImage {
  file: File;
  redactionCount: number;
}

/**
 * Every coordinate in this component is a top-left corner.
 *
 * Stated explicitly rather than inherited, because **Fabric defaults
 * `originX`/`originY` to `center`**. Left to the default, `left`/`top` mean
 * the *middle* of the object, and two things break at once: the background
 * image is drawn centred on (0,0) so only its bottom-right quarter is
 * visible, and every redaction rectangle is offset from where it was dragged
 * — while `flatten()` burns the black box at the top-left interpretation.
 * That last one is the dangerous case: the reporter covers a name, and the
 * exported file has the name uncovered with a black box beside it.
 */
const TOP_LEFT = { originX: 'left', originY: 'top' } as const;

/** Ignore a click that merely selected something. */
const MIN_REDACTION_PX = 6;

/** Long enough for any real decode, short enough not to strand the reporter. */
const DECODE_TIMEOUT_MS = 3000;

/** Fits the editing surface on screen without touching the exported bitmap. */
const DISPLAY_MARGIN_PX = 220;

/**
 * The mandatory step between capturing an image and attaching it.
 *
 * The reporter drags opaque black rectangles over anything identifying, and
 * the result is flattened into a new PNG. Two properties matter and both are
 * structural rather than promised:
 *
 * 1. **The redaction is irreversible.** Solid fill, never blur or pixelation —
 *    both of those are partially reversible, and a reversible redaction of a
 *    patient identifier is worse than none because it looks safe. What leaves
 *    this component is a bitmap in which the covered pixels are simply black;
 *    there is no layer, no object list, nothing to undo.
 * 2. **The original never leaves.** The unredacted capture exists only as an
 *    `HTMLImageElement` held here and released on destroy. The only thing this
 *    component emits is the flattened file.
 *
 * The flattening is done with plain canvas calls rather than Fabric's
 * exporter. Fabric is the editing surface; the burn is fifteen lines of our
 * own code, because for the one operation the feature exists to guarantee it
 * should be possible to read exactly what produces the bytes.
 */
@Component({
  selector: 'o3-support-screenshot-redactor',
  imports: [TranslatePipe],
  templateUrl: './screenshot-redactor.html',
  styleUrl: './screenshot-redactor.scss',
})
export class ScreenshotRedactor implements OnDestroy {
  private readonly i18n = inject(I18N);

  /** The image to redact — a fresh capture, or a file the reporter picked. */
  source = input.required<Blob>();

  /** Carried through to the upload so the console shows a recognisable name. */
  fileName = input<string>('screenshot.png');

  /** The flattened PNG. The only way an image gets out of here. */
  applied = output<IRedactedImage>();
  cancelled = output<void>();

  private readonly canvasElement =
    viewChild.required<ElementRef<HTMLCanvasElement>>('canvasEl');

  loading = signal(true);
  exporting = signal(false);
  failed = signal(false);

  /** How many areas are covered. Shown, and recorded with the upload. */
  redactionCount = signal(0);

  /**
   * The reporter's explicit statement that the image is clean.
   *
   * Deliberately not "at least one rectangle". Plenty of screenshots contain
   * no patient data at all, and demanding a rectangle would teach people to
   * draw one in an empty corner and stop looking — which is precisely the
   * habit this step exists to prevent. The acknowledgement is the control;
   * the count is only the record.
   */
  acknowledged = signal(false);

  canApply = computed(
    () => this.acknowledged() && !this.loading() && !this.exporting(),
  );

  private canvas: Canvas | null = null;
  private sourceImage: HTMLImageElement | null = null;
  private objectUrl: string | null = null;
  private drawing: Rect | null = null;
  private origin = { x: 0, y: 0 };

  constructor() {
    // Mounted from the globally-hosted capture widget rather than a route, so
    // there is no `i18nResolver` to load these — the `MessagingSlideIn`
    // pattern, as used by the widget itself.
    this.i18n.addResourceBundle('en', 'Support_TicketCapture', enTranslations, true, true);
    this.i18n.addResourceBundle('ro', 'Support_TicketCapture', roTranslations, true, true);

    effect(() => {
      const blob = this.source();
      void this.load(blob);
    });
  }

  ngOnDestroy(): void {
    this.release();
  }

  /** Removes the most recent area, which is what "undo" means with one tool. */
  undo(): void {
    const rects = this.rects();
    const last = rects[rects.length - 1];
    if (!last) return;

    this.canvas?.remove(last);
    this.canvas?.requestRenderAll();
    this.syncCount();
  }

  clear(): void {
    for (const rect of this.rects()) this.canvas?.remove(rect);
    this.canvas?.requestRenderAll();
    this.syncCount();
  }

  onCancel(): void {
    this.cancelled.emit();
  }

  /**
   * Flattens and emits.
   *
   * Every rectangle is axis-aligned — rotation is locked, and resizes are
   * normalised into width/height as they happen — so a `fillRect` per area is
   * an exact reproduction of what the reporter saw, at the bitmap's own
   * resolution rather than the size it happened to be displayed at.
   */
  async apply(): Promise<void> {
    if (!this.canApply() || !this.sourceImage) return;

    this.exporting.set(true);
    try {
      const count = this.redactionCount();
      const blob = await this.flatten(this.sourceImage);
      this.applied.emit({
        file: new File([blob], this.fileName(), { type: 'image/png' }),
        redactionCount: count,
      });
    } catch {
      this.failed.set(true);
    } finally {
      this.exporting.set(false);
    }
  }

  private async load(blob: Blob): Promise<void> {
    this.release();
    this.loading.set(true);
    this.failed.set(false);

    try {
      const image = await this.decode(blob);
      this.sourceImage = image;

      const { Canvas, FabricImage } = await import('fabric');
      const element = this.canvasElement().nativeElement;

      const canvas = new Canvas(element, {
        selection: false,
        preserveObjectStacking: true,
        uniformScaling: false,
      });

      // The backing store stays at the bitmap's own size, so every object
      // coordinate *is* an image coordinate and the export needs no
      // conversion. Only the CSS size shrinks to fit the viewport; Fabric
      // accounts for that ratio when it maps pointer events.
      canvas.setDimensions({ width: image.naturalWidth, height: image.naturalHeight });
      const scale = this.displayScale(image);
      canvas.setDimensions(
        {
          width: `${Math.round(image.naturalWidth * scale)}px`,
          height: `${Math.round(image.naturalHeight * scale)}px`,
        },
        { cssOnly: true },
      );

      canvas.backgroundImage = new FabricImage(image, {
        ...TOP_LEFT,
        left: 0,
        top: 0,
      });
      canvas.requestRenderAll();

      this.wire(canvas);
      this.canvas = canvas;
      this.loading.set(false);
    } catch {
      this.failed.set(true);
      this.loading.set(false);
    }
  }

  private wire(canvas: Canvas): void {
    canvas.on('mouse:down', (event) => {
      // Clicking an existing rectangle selects it; only empty space starts a
      // new one, or every attempt to nudge an area would stack another on top.
      if (canvas.getActiveObject() || event.target) return;

      const point = canvas.getScenePoint(event.e);
      this.origin = { x: point.x, y: point.y };
      void this.beginRect(canvas, point.x, point.y);
    });

    canvas.on('mouse:move', (event) => {
      if (!this.drawing) return;

      const point = canvas.getScenePoint(event.e);
      this.drawing.set({
        left: Math.min(this.origin.x, point.x),
        top: Math.min(this.origin.y, point.y),
        width: Math.abs(point.x - this.origin.x),
        height: Math.abs(point.y - this.origin.y),
      });
      canvas.requestRenderAll();
    });

    canvas.on('mouse:up', () => {
      const rect = this.drawing;
      this.drawing = null;
      if (!rect) return;

      // A click that dragged nowhere is a click, not a redaction.
      if (rect.width < MIN_REDACTION_PX || rect.height < MIN_REDACTION_PX) {
        canvas.remove(rect);
      } else {
        rect.setCoords();
        canvas.setActiveObject(rect);
      }
      canvas.requestRenderAll();
      this.syncCount();
    });

    // Bake a resize back into width/height immediately. Leaving it in
    // `scaleX`/`scaleY` would mean the export had to reproduce Fabric's
    // transform maths, which is exactly the kind of subtlety that turns into
    // a black box in the wrong place.
    canvas.on('object:modified', ({ target }) => {
      if (!target) return;
      target.set({
        width: Math.abs(target.width * target.scaleX),
        height: Math.abs(target.height * target.scaleY),
        scaleX: 1,
        scaleY: 1,
      });
      target.setCoords();
      canvas.requestRenderAll();
    });
  }

  private async beginRect(canvas: Canvas, x: number, y: number): Promise<void> {
    const { Rect } = await import('fabric');
    const selectionColour = paletteColour('--color-error-500', '#f04438');

    const rect = new Rect({
      ...TOP_LEFT,
      left: x,
      top: y,
      width: 0,
      height: 0,
      // The one colour in this component that is deliberately a literal and
      // must never become a token. A redaction is opaque black because that
      // is what destroys the pixels underneath; a theme that redefined it —
      // to a dark grey, to something with alpha, to a light value in a dark
      // mode — would silently turn every redaction back into readable data.
      fill: '#000000',
      opacity: 1,
      objectCaching: false,
      lockRotation: true,
      // Selection chrome, which is ordinary UI and does follow the theme.
      borderColor: selectionColour,
      cornerColor: selectionColour,
      cornerSize: 10,
      transparentCorners: false,
    });
    rect.setControlsVisibility({ mtr: false });

    canvas.add(rect);
    this.drawing = rect;
  }

  /**
   * Draw the original, then paint every area solid black over it.
   *
   * The result is a new bitmap. The rectangles are not objects in it, not a
   * layer, and not metadata — they are black pixels, and what they covered is
   * not in the file in any form.
   */
  private async flatten(image: HTMLImageElement): Promise<Blob> {
    const target = document.createElement('canvas');
    target.width = image.naturalWidth;
    target.height = image.naturalHeight;

    const context = target.getContext('2d');
    if (!context) throw new Error('2D context unavailable');

    context.drawImage(image, 0, 0);
    // Literal, and deliberately not a token — see the rectangle's `fill`.
    // This is the line that destroys the pixels; a theme must not reach it.
    context.fillStyle = '#000000';
    for (const rect of this.rects()) {
      context.fillRect(
        Math.round(rect.left),
        Math.round(rect.top),
        Math.round(rect.width * rect.scaleX),
        Math.round(rect.height * rect.scaleY),
      );
    }

    return await new Promise<Blob>((resolve, reject) => {
      target.toBlob(
        (blob) =>
          blob ? resolve(blob) : reject(new Error('Could not encode the PNG')),
        'image/png',
      );
    });
  }

  private rects(): Rect[] {
    return (this.canvas?.getObjects() ?? []) as Rect[];
  }

  private syncCount(): void {
    this.redactionCount.set(this.rects().length);
  }

  /** Fit to the viewport, never enlarge — an upscaled bitmap redacts no better. */
  private displayScale(image: HTMLImageElement): number {
    const availableWidth = Math.max(320, window.innerWidth - 64);
    const availableHeight = Math.max(240, window.innerHeight - DISPLAY_MARGIN_PX);
    return Math.min(
      1,
      availableWidth / image.naturalWidth,
      availableHeight / image.naturalHeight,
    );
  }

  private decode(blob: Blob): Promise<HTMLImageElement> {
    const url = URL.createObjectURL(blob);
    this.objectUrl = url;

    return new Promise((resolve, reject) => {
      const image = new Image();
      // Bounded: a decoder that neither loads nor errors would leave the
      // reporter on "preparing the image" with no way forward. Failing says
      // so and lets them cancel.
      const timeout = setTimeout(
        () => reject(new Error('Timed out decoding the image')),
        DECODE_TIMEOUT_MS,
      );
      image.onload = () => {
        clearTimeout(timeout);
        resolve(image);
      };
      image.onerror = () => {
        clearTimeout(timeout);
        reject(new Error('Not a decodable image'));
      };
      image.src = url;
    });
  }

  /** Drops the unredacted bitmap. Called on every reload and on destroy. */
  private release(): void {
    this.canvas?.dispose();
    this.canvas = null;
    this.drawing = null;
    this.sourceImage = null;
    this.redactionCount.set(0);

    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
    }
  }
}
